
import { Calendar, ExternalLink, MessageSquare, ThumbsDown, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonOutlineClass,
  painelModalCancelButtonClass,
  workspaceModalInsetClass,
} from '@/components/painel/painel-styles';
import type { GuideNegativeFeedback } from '@/lib/guide-feedback';
import { GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH } from '@/lib/guide-feedback';
import { getGuideCategoryPath } from '@/lib/guide-routes';
import { cn } from '@/lib/utils';

const FEEDBACK_MODAL_VISUAL = {
  icon: MessageSquare,
  iconShell: 'bg-gradient-to-br from-violet-500/22 to-violet-700/10 ring-violet-400/30',
  iconColor: 'text-violet-200',
  glow: 'shadow-[0_0_40px_-10px_rgba(167,139,250,0.3)]',
};

function formatReceivedAt(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'full',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

type PainelGuideFeedbackMessageModalProps = {
  item: GuideNegativeFeedback | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PainelGuideFeedbackMessageModal({
  item,
  open,
  onOpenChange,
}: PainelGuideFeedbackMessageModalProps) {
  const Icon = FEEDBACK_MODAL_VISUAL.icon;
  const publicHref = item
    ? `/${getGuideCategoryPath(item.categoryId)}/${item.slug}`
    : '#';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-2xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">
          {item ? `Feedback sobre ${item.articleTitle}` : 'Feedback de visitante'}
        </DialogTitle>
        {item ? (
          <PainelPermissionsDialogShell>
            <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
              <div className="flex items-start gap-4 pr-14">
                <span
                  className={cn(
                    'inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1',
                    FEEDBACK_MODAL_VISUAL.iconShell,
                    FEEDBACK_MODAL_VISUAL.glow
                  )}
                >
                  <Icon className={cn('h-7 w-7', FEEDBACK_MODAL_VISUAL.iconColor)} aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300/55">
                    Preciso de mais ajuda
                  </p>
                  <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                    {item.articleTitle}
                  </h2>
                  <p className="mt-1.5 font-inter text-sm text-slate-400">
                    Mensagem enviada por um visitante quando o tutorial não foi suficiente.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5 px-6 py-6 sm:px-8">
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/25 bg-rose-500/10 px-3 py-1 font-poppins text-[11px] font-semibold text-rose-100">
                  <ThumbsDown className="h-3.5 w-3.5" />
                  Não ajudou
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-inter text-[11px] text-slate-300">
                  <Calendar className="h-3.5 w-3.5 text-slate-500" />
                  {formatReceivedAt(item.createdAt)}
                </span>
                {item.xatUsername ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/25 bg-sky-500/10 px-3 py-1 font-mono text-[11px] font-semibold text-sky-100">
                    <User className="h-3.5 w-3.5 text-sky-300" />
                    @{item.xatUsername}
                  </span>
                ) : null}
              </div>

              {item.xatUsername ? (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                    Username no xat
                  </p>
                  <p className="mt-1 font-mono text-sm text-sky-200">@{item.xatUsername}</p>
                </div>
              ) : null}

              <div>
                <p className="mb-2 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Mensagem do visitante
                </p>
                <div className={cn(workspaceModalInsetClass, 'p-4 sm:p-5')}>
                  <p className="whitespace-pre-wrap font-inter text-sm leading-relaxed text-slate-200">
                    {item.message}
                  </p>
                </div>
                <p className="mt-2 text-right font-inter text-[11px] text-slate-500">
                  {item.message.length} / {GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH} caracteres
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  Artigo público
                </p>
                <a
                  href={publicHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1.5 font-mono text-sm text-sky-300 hover:text-sky-200"
                >
                  /{getGuideCategoryPath(item.categoryId)}/{item.slug}
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" />
                </a>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:px-8">
              <Button
                type="button"
                variant="ghost"
                className={painelModalCancelButtonClass}
                onClick={() => onOpenChange(false)}
              >
                Fechar
              </Button>
              <Button type="button" className={painelButtonOutlineClass} asChild>
                <a href={publicHref} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Ver no site
                </a>
              </Button>
            </div>
          </PainelPermissionsDialogShell>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
